import { useEffect, useState } from 'react';

export const LABEL_MODES = {
  NAME_ONLY: 'name',
  NUMBER_ONLY: 'number',
  NAME_AND_NUMBER: 'name-number',
};

const STORAGE_KEY = 'qrLabelMode';
const VALID_MODES = new Set(Object.values(LABEL_MODES));
const DEFAULT_MODE = LABEL_MODES.NAME_AND_NUMBER;

function sanitizeMode(value) {
  return VALID_MODES.has(value) ? value : DEFAULT_MODE;
}

function readStoredMode() {
  if (typeof window === 'undefined') return DEFAULT_MODE;
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return sanitizeMode(stored);
  } catch {
    return DEFAULT_MODE;
  }
}

export function useLabelMode() {
  const [mode, setMode] = useState(readStoredMode);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(STORAGE_KEY, mode);
    } catch {
      // ignore storage errors (e.g. Safari private mode)
    }
  }, [mode]);

  const updateMode = (next) => {
    setMode((prev) => {
      const resolved = typeof next === 'function' ? next(prev) : next;
      return sanitizeMode(resolved);
    });
  };

  return [mode, updateMode];
}

export function getLabelLines(mode, name, number) {
  const safeName = (name || '').trim();
  const safeNumber = (number || '').trim();
  const hasName = safeName.length > 0;
  const hasNumber = safeNumber.length > 0;

  if (mode === LABEL_MODES.NAME_ONLY) {
    if (hasName) return [safeName];
    return hasNumber ? [safeNumber] : [];
  }

  if (mode === LABEL_MODES.NUMBER_ONLY) {
    if (hasNumber) return [safeNumber];
    return hasName ? [safeName] : [];
  }

  const lines = [];
  if (hasName) lines.push(safeName);
  if (hasNumber && (!hasName || safeNumber !== safeName)) lines.push(safeNumber);

  if (lines.length === 0) {
    if (hasName) return [safeName];
    if (hasNumber) return [safeNumber];
  }
  return lines;
}

export function getLabelText(mode, name, number, separator = '\n') {
  return getLabelLines(mode, name, number).join(separator);
}